import { formatISO, parseISO } from 'date-fns';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import AddEditWarehouse from '@/modules/settings/property/components/Warehouses/AddEditWarehouse';
import {
  useGetProductsSuppliersList,
  useGetWarehouses,
} from '@/modules/warehouseManagement/shared/apiHooks';
import { IWarehouseManagement } from '@/modules/warehouseManagement/shared/types';
import CreateSupplier from '@/modules/warehouseManagement/warehouses/components/InboundDelivery/CreateEditInboundDelivery/Modals/CreateSupplier';
import { InputField, SelectField, Typography } from '@/shared/components/atoms';
import CalendarDatePicker from '@/shared/components/atoms/CalendarDatePicker';
import TooltipLite from '@/shared/components/atoms/TooltipLite';
import { permissions } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { IGeneral } from '@/shared/types';

import InfoIcon from '~/images/shared/ExleminationMark.svg';
import WarehouseIcon from '~/images/shared/Packhouse.svg';

const InboundDeliveryGeneralInfo = () => {
  const { t } = useTranslation();

  const { openModal } = useModal();

  const [SelectedAsset] = useStore((store) => store?.asset);

  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<IWarehouseManagement.ICreateInboundDeliveryFormValues>();

  const { data: warehouses, isLoading: isLoadingWarehouses } = useGetWarehouses(
    {
      entityId: SelectedAsset?.id,
      isInventoryActive: !!SelectedAsset?.IsInventoryActive,
      paginated: false,
    }
  );

  const { data: productSuppliers, isLoading: isLoadingSuppliers } =
    useGetProductsSuppliersList();

  const { user } = useAuth();

  const hasPermissionsToAddWarehouse = UserHasPermission({
    action: permissions.CAN_ADD_WAREHOUSE,
    role: user?.userGroupId,
  });

  return (
    <div className='flex flex-col gap-24'>
      <div className='border-b-xs border-b-outline-default pb-24'>
        <div className='mb-16 flex items-center gap-8'>
          <span className='text-primary-default'>
            <WarehouseIcon width='24' height='24' />
          </span>
          <Typography weight='semiBold'>{t('warehouse')}</Typography>
          <TooltipLite content={t('warehouse-not-found')}>
            <span className='cursor-pointer text-primary-default'>
              <InfoIcon width='16' height='16' />
            </span>
          </TooltipLite>
        </div>
        <SelectField
          id='warehouse'
          errorMessageId='warehouse.id'
          isDisabled={!!watch('products')?.[0]?.categoryId}
          getLabel={(warehouse: IWarehouseManagement.WarehouseObjectSchema) =>
            warehouse.name
          }
          getValue={(
            warehouse: IWarehouseManagement.WarehouseObjectSchema
          ) => ({
            name: warehouse.name,
            id: warehouse.id,
          })}
          value={warehouses?.items.filter(
            (item) => item.id === watch('warehouse')?.id
          )}
          createOption={
            hasPermissionsToAddWarehouse
              ? {
                  title: t('add-warehouse'),
                  subTitle: t('add-warehouse-sub-title'),
                  onClick: () =>
                    openModal(<AddEditWarehouse property={SelectedAsset} />, {
                      variant: 'full',
                      title: (
                        <Typography variant='h2' weight='bold'>
                          {t('add-warehouse')}
                        </Typography>
                      ),
                    }),
                }
              : undefined
          }
          isLoading={isLoadingWarehouses}
          borderColor={themeColors().outline.variant}
          placeholder={t('warehouse')}
          list={warehouses?.items ?? []}
          py='0.5rem'
          px='0.5rem'
          borderRadius='1rem'
          width='w-full min-w-0'
        />
      </div>
      <SelectField
        id='supplier'
        errorMessageId='supplier.id'
        getLabel={(supplier: IGeneral.NameIdPair) => supplier.name}
        getValue={(supplier: IGeneral.NameIdPair) => ({
          name: supplier.name,
          id: supplier.id,
        })}
        createOption={{
          title: t('create-new-supplier'),
          onClick: () =>
            openModal(<CreateSupplier />, {
              title: t('create-new-supplier'),
            }),
        }}
        value={productSuppliers?.items.filter(
          (item) => item.id === watch('supplier')?.id
        )}
        isLoading={isLoadingSuppliers}
        borderColor={themeColors().outline.variant}
        label={t('the-supplier')}
        placeholder={t('the-supplier')}
        list={productSuppliers?.items ?? []}
        py='0.5rem'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full min-w-0'
      />
      <InputField
        id='supplyOrder'
        {...register('supplyOrder', {
          setValueAs: (value) => (value?.length ? value : undefined),
        })}
        label={`${t('supply-order-number')} (${t('optional')})`}
        placeholder={t('supply-order-number')}
        error={!!errors?.supplyOrder}
        errorMessage={errors.supplyOrder?.message}
        className='w-full !rounded-xl bg-background-transparent desktop:max-h-56'
        labelClasseName='!text-14'
        borderColor='border-outline-variant'
        textColor='text-text-default'
        width='w-full'
        py='!py-14'
      />
      <Controller
        name='depositOrderDate'
        render={({ field: { onChange, value } }) => (
          <CalendarDatePicker
            label={t('inbound-delivery-date')}
            placeholder={t('inbound-delivery-date')}
            maxDate={new Date()}
            onChange={(value) => {
              if (value)
                onChange(formatISO(value as Date, { representation: 'date' }));
            }}
            value={value ? parseISO(value) : undefined}
            error={errors?.depositOrderDate?.message}
          />
        )}
      />
    </div>
  );
};

export default InboundDeliveryGeneralInfo;
